import { useEffect, useRef, useState } from 'react'
import { Icon } from '@iconify/react'
import { AchievementCategory } from './AchievementCategory'
import { MONTHLY_MEDALS as MEDALS } from '@/lib/monthly-medals'
import styles from './LandingProfileAchievements.module.css'
import type { MonthlyMedalAchievement } from '@/lib/landing-achievements'

export function MonthlyMedals({
  achievements = [],
  onViewPrizes = () => {},
}: {
  achievements?: MonthlyMedalAchievement[]
  campaignId?: number
  donorId?: number
  onViewPrizes?: () => void
}) {
  const [selected, setSelected] = useState<(typeof MEDALS)[number] | null>(null)
  const dialog = useRef<HTMLDialogElement>(null)
  const achievement = achievements.find((item) => item.id === selected?.id)
  const close = () => {
    setSelected(null)
  }
  useEffect(() => {
    const element = dialog.current
    if (!selected || !element) return
    const previousFocus = document.activeElement as HTMLElement | null
    element.showModal()
    return () => {
      element.close()
      previousFocus?.focus()
    }
  }, [selected])
  return (
    <>
      <AchievementCategory
        title="Medalhas"
        earnedCount={achievements.filter((item) => item.unlocked).length}
        totalCount={MEDALS.length}
        description={`Duas consultas veterinárias pagas no mesmo mês, nesta campanha, conquistam a medalha. Coleção de ${achievements[0]?.period.slice(0, 4) ?? new Date().getFullYear()}.`}
      >
        {MEDALS.map((medal) => (
          <button
            key={medal.month}
            type="button"
            className={
              achievements.some((item) => item.id === medal.id && item.unlocked)
                ? styles.badgeUnlocked
                : styles.badgeLocked
            }
            aria-label={`${medal.month}: ${achievements.some((item) => item.id === medal.id && item.unlocked) ? 'conquistada' : 'bloqueada'}`}
            onClick={() => setSelected(medal)}
          >
            <img src={medal.image} alt="" className={styles.badgeImage} loading="lazy" />
            <span className={styles.badgeName}>{medal.month}</span>
            <small>
              {achievements.find((item) => item.id === medal.id)?.current_value ?? 0}/2
              consultas
            </small>
          </button>
        ))}
      </AchievementCategory>
      {selected && (
        <dialog
          ref={dialog}
          className={styles.medalDialog}
          aria-labelledby="monthly-medal-title"
          onCancel={(event) => {
            event.preventDefault()
            close()
          }}
          onClick={(event) => {
            if (event.target === event.currentTarget) close()
          }}
        >
          <button
            type="button"
            className={styles.close}
            aria-label="Fechar medalha"
            autoFocus
            onClick={close}
          >
            <Icon icon="mdi:close" />
          </button>
          <p className={styles.celebrationKicker}>
            {achievement?.unlocked ? 'Medalha conquistada!' : 'Cuidado que vira apoio'}
          </p>
          <img src={selected.image} alt="" className={styles.detailImage} />
          <h2 id="monthly-medal-title">
            {achievement?.unlocked
              ? achievement.content?.title || `Medalha de ${selected.month}`
              : `Medalha de ${selected.month}`}
          </h2>
          <p className={styles.detailText}>
            {achievement?.unlocked
              ? achievement.content?.text ||
                'Você cuidou do seu mascote e apoiou a entidade com duas compras neste mês!'
              : achievement?.locked_reason ||
                'Leve seu mascote ao veterinário e confirme duas compras nesta campanha no mesmo mês. Cada consulta com remédio restaura a saúde a 100% e gera os benefícios configurados da campanha.'}
          </p>
          <p>
            {achievement?.current_value ?? 0}/2 consultas ·{' '}
            {achievement?.period ?? selected.month}
          </p>
          {achievement?.unlocked && (
            <>
              {achievement.rewards?.lucky_numbers ? (
                <p>{achievement.rewards.lucky_numbers} números extras pela medalha</p>
              ) : null}
              {achievement.rewards?.roulette_spins ? (
                <p>{achievement.rewards.roulette_spins} giros extras na roleta</p>
              ) : null}
              {achievement.rewards?.coupon_percent ? (
                <p>Cupom de {achievement.rewards.coupon_percent}% de desconto</p>
              ) : null}
              {achievement.content?.video_url && (
                <div className={styles.videoFrame}>
                  <iframe
                    src={achievement.content.video_url}
                    title={`Vídeo da medalha de ${selected.month}`}
                    referrerPolicy="strict-origin-when-cross-origin"
                    sandbox="allow-scripts allow-same-origin"
                    loading="lazy"
                  />
                </div>
              )}
              <button
                className={styles.cta}
                onClick={() => {
                  close()
                  onViewPrizes()
                }}
              >
                Ver meus benefícios
              </button>
            </>
          )}
        </dialog>
      )}
    </>
  )
}
